import React from "react"
import PropTypes from "prop-types"
import { Col, Flex, Form, Input, Radio, Row, Select } from "antd"
import { Trans, useTranslation } from "react-i18next"
import { CardCvcElement, CardExpiryElement, CardNumberElement, PaymentRequestButtonElement } from "@stripe/react-stripe-js"
import { PayPalButtons } from "@paypal/react-paypal-js"
import { countries } from "country-codes-flags-phone-codes"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import { PAYMENT_METHODS } from "./payment"
import PaymentMethodIcon from "./PaymentMethodIcon"
import styles from "./WidgetCheckout.module.css"

const OPTION_KEYS = ["full", "cash", "later"]

// Stripe Elements report completeness through onChange; mirror it into the form so the required
// rules work like the old widget's.
const markComplete = (form, field) => (event) => form.setFieldValue(field, event.complete ? true : undefined)

const CardFields = ({ form }) => {
  const { t } = useTranslation()
  return (
    <div className={styles.cardFields}>
      <Row gutter={[12, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item label={t("booking_widget.checkout.card_number")} name="cardNumber" rules={[{ required: true, message: t("booking_widget.checkout.errors.card_number") }]}>
            <div className={styles.stripeInput}>
              <CardNumberElement options={{ showIcon: true, disableLink: true }} onChange={markComplete(form, "cardNumber")} />
            </div>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label={t("booking_widget.checkout.card_name")} name="cardName" rules={[{ required: true, message: t("booking_widget.checkout.errors.card_name") }]}>
            <Input size="large" placeholder={t("booking_widget.checkout.card_name_placeholder")} />
          </Form.Item>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Item label={t("booking_widget.checkout.expiry")} name="expiry" rules={[{ required: true, message: t("booking_widget.checkout.errors.expiry") }]}>
            <div className={styles.stripeInput}>
              <CardExpiryElement onChange={markComplete(form, "expiry")} />
            </div>
          </Form.Item>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Item label={t("booking_widget.checkout.cvc")} name="cvc" rules={[{ required: true, message: t("booking_widget.checkout.errors.cvc") }]}>
            <div className={styles.stripeInput}>
              <CardCvcElement onChange={markComplete(form, "cvc")} />
            </div>
          </Form.Item>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Item label={t("booking_widget.checkout.country")} name="country" initialValue="United States">
            <Select
              size="large"
              showSearch
              placeholder={t("booking_widget.checkout.country_placeholder")}
              options={countries.map((country) => ({ value: country.name, label: country.name }))}
            />
          </Form.Item>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Item label={t("booking_widget.checkout.zip")} name="zip" rules={[{ required: true, message: t("booking_widget.checkout.errors.zip") }]}>
            <Input size="large" />
          </Form.Item>
        </Col>
      </Row>
      <span className={styles.hint}>{t("booking_widget.checkout.powered_by_stripe")}</span>
    </div>
  )
}

CardFields.propTypes = {
  form: PropTypes.object.isRequired
}

const MethodRow = ({ value, label, icon, isSelected, isDisabled, hint, children }) => (
  <div className={`${styles.methodRow} ${isSelected ? styles.methodRowSelected : ""}`}>
    <Radio value={value} disabled={isDisabled} className={styles.methodRadio}>
      <Flex align="center" gap={12}>
        {icon}
        <Flex vertical>
          <span>{label}</span>
          {hint && <span className={styles.hint}>{hint}</span>}
        </Flex>
      </Flex>
    </Radio>
    {isSelected && children}
  </div>
)

MethodRow.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hint: PropTypes.string,
  children: PropTypes.node
}

// "How would you like to pay?" + payment method. Deposit options appear only when allowed, and the
// balance-in-cash one only when the experience takes cash; the wallets (shown only when the browser
// has one) charge the total today, as in the old widget.
const PaymentSection = ({ form, amounts, canPayDeposit, canPayCash, paymentOption, paymentMethod, wallet, isPayPalAvailable, paypal, isPaying }) => {
  const { t } = useTranslation()
  const options = canPayDeposit ? OPTION_KEYS.filter((option) => option !== "cash" || canPayCash) : ["full"]
  const isWalletAllowed = paymentOption === "full"

  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>{t("booking_widget.checkout.payment_title")}</h2>
      <Flex vertical gap={12}>
        <span className={styles.groupLabel}>{t("booking_widget.checkout.how_to_pay")}</span>
        <Form.Item name="paymentOption" initialValue="full" noStyle>
          <Radio.Group className={styles.radioStack}>
            {options.map((option) => (
              <Radio key={option} value={option} className={`${styles.optionCard} ${paymentOption === option ? styles.optionCardSelected : ""}`}>
                <Flex vertical gap={2}>
                  <span>{t(`booking_widget.checkout.option_${option}_title`)}</span>
                  <span className={styles.optionText}>
                    <Trans
                      i18nKey={`booking_widget.checkout.option_${option}_text`}
                      values={{ amount: numberToCurrency(amounts.chargeNowByOption[option]), balance: numberToCurrency(amounts.balanceByOption[option]) }}
                      components={{ b: <strong /> }}
                    />
                  </span>
                </Flex>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Flex>

      <Flex vertical gap={12} className="mt-l">
        <span className={styles.groupLabel}>{t("booking_widget.checkout.payment_method")}</span>
        <Form.Item name="paymentMethod" initialValue={PAYMENT_METHODS.card} noStyle>
          <Radio.Group className={`${styles.radioStack} ${styles.methodList}`}>
            <MethodRow
              value={PAYMENT_METHODS.card}
              label={t("booking_widget.checkout.method_card")}
              icon={<PaymentMethodIcon method={PAYMENT_METHODS.card} />}
              isSelected={paymentMethod === PAYMENT_METHODS.card}
            >
              <CardFields form={form} />
            </MethodRow>
            {wallet.isGooglePay && (
              <MethodRow
                value={PAYMENT_METHODS.googlePay}
                label={t("booking_widget.checkout.method_google_pay")}
                icon={<PaymentMethodIcon method={PAYMENT_METHODS.googlePay} />}
                isSelected={paymentMethod === PAYMENT_METHODS.googlePay}
                isDisabled={!isWalletAllowed}
                hint={isWalletAllowed ? null : t("booking_widget.checkout.wallet_full_only")}
              />
            )}
            {wallet.isApplePay && (
              <MethodRow
                value={PAYMENT_METHODS.applePay}
                label={t("booking_widget.checkout.method_apple_pay")}
                icon={<PaymentMethodIcon method={PAYMENT_METHODS.applePay} />}
                isSelected={paymentMethod === PAYMENT_METHODS.applePay}
                isDisabled={!isWalletAllowed}
                hint={isWalletAllowed ? null : t("booking_widget.checkout.wallet_full_only")}
              />
            )}
            {isPayPalAvailable && (
              <MethodRow
                value={PAYMENT_METHODS.paypal}
                label={t("booking_widget.checkout.method_paypal")}
                icon={<span className={styles.methodBadge}><Icon type="feather" name="DollarSign" width={18} height={18} color="currentColor" /></span>}
                isSelected={paymentMethod === PAYMENT_METHODS.paypal}
              />
            )}
          </Radio.Group>
        </Form.Item>
      </Flex>

      {/* The wallet and PayPal buttons replace Confirm and Pay for those methods. */}
      {[PAYMENT_METHODS.googlePay, PAYMENT_METHODS.applePay].includes(paymentMethod) && wallet.paymentRequest && isWalletAllowed && (
        <div className="mt-m">
          <PaymentRequestButtonElement options={{ paymentRequest: wallet.paymentRequest }} onClick={wallet.onClick} />
        </div>
      )}
      {paymentMethod === PAYMENT_METHODS.paypal && isPayPalAvailable && (
        <div className="mt-m">
          <span className={styles.hint}>{t("booking_widget.checkout.paypal_hint")}</span>
          <PayPalButtons
            fundingSource="paypal"
            forceReRender={[amounts.chargeNow, paymentOption]}
            onClick={paypal.onClick}
            createOrder={paypal.createOrder}
            onApprove={paypal.onApprove}
            onError={paypal.onError}
            disabled={isPaying}
            className="mt-xs"
          />
        </div>
      )}
    </section>
  )
}

PaymentSection.propTypes = {
  form: PropTypes.object.isRequired,
  amounts: PropTypes.shape({
    chargeNow: PropTypes.number,
    chargeNowByOption: PropTypes.object,
    balanceByOption: PropTypes.object
  }).isRequired,
  canPayDeposit: PropTypes.bool,
  canPayCash: PropTypes.bool,
  paymentOption: PropTypes.string,
  paymentMethod: PropTypes.string,
  wallet: PropTypes.shape({
    isGooglePay: PropTypes.bool,
    isApplePay: PropTypes.bool,
    paymentRequest: PropTypes.object,
    onClick: PropTypes.func
  }).isRequired,
  isPayPalAvailable: PropTypes.bool,
  paypal: PropTypes.shape({ onClick: PropTypes.func, createOrder: PropTypes.func, onApprove: PropTypes.func, onError: PropTypes.func }).isRequired,
  isPaying: PropTypes.bool
}

PaymentSection.defaultProps = {
  canPayDeposit: false,
  canPayCash: true,
  paymentOption: "full",
  paymentMethod: PAYMENT_METHODS.card,
  isPayPalAvailable: false,
  isPaying: false
}

export default PaymentSection
